import React, { useRef, useState } from 'react';
import { Database, Upload, File, Loader2, CheckCircle, Trash2, Globe, Download, Link2 } from 'lucide-react';

const KnowledgeBaseTab = ({ 
  agent, 
  setAgent,
  agentId,
  hasFeature,
  getLimit,
  handleFileUpload,
  removeKnowledgeFile,
  uploadingFiles
}) => {
  const fileInputRef = useRef(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [addingWebsite, setAddingWebsite] = useState(false);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!hasFeature('knowledge_base')) {
    return (
      <div className="space-y-8">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-8 text-center">
          <Database className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Knowledge Base Disabled</h3>
          <p className="text-gray-400">This feature has been disabled by your administrator.</p>
          <p className="text-gray-500 text-sm mt-2">Contact your admin to enable Knowledge Base access.</p>
        </div>
      </div>
    );
  }

  const maxSize = getLimit('knowledge_base_size_mb') || 50;
  const usedPercentage = (agent.knowledgeBaseSize / (maxSize * 1024 * 1024)) * 100;
  const isNearLimit = usedPercentage >= 70;
  const isAtLimit = usedPercentage >= 90;

  return (
    <div className="space-y-8">
      {/* Upload and Website sections side by side */}
      <div className="grid grid-cols-2 gap-6">
        {/* Upload PDFs Section */}
        <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Upload className="w-5 h-5 text-teal-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Upload PDFs</h2>
              <p className="text-gray-500 text-sm">Add PDF files to your assistant&apos;s knowledge base</p>
            </div>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
          
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-gray-600 transition-colors"
          >
            <Upload className="w-10 h-10 text-teal-400 mx-auto mb-3" />
            <p className="text-white mb-1">Drag and drop a file here, or click to select</p>
            <p className="text-gray-500 text-sm">Supported formats: PDF (max 10MB)</p>
          </div>
        </section>

        {/* Website Knowledge Base Section */}
        <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-5 h-5 text-teal-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Website Knowledge Base</h2>
              <p className="text-gray-500 text-sm">Add website content to your assistant&apos;s knowledge base</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <label className="text-gray-300 text-sm">Website URL</label>
              <span className="w-4 h-4 rounded-full border border-gray-500 inline-flex items-center justify-center text-xs text-gray-500">i</span>
            </div>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com/"
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
            />
            <button
              onClick={() => {
                if (websiteUrl) {
                  setAddingWebsite(true);
                  // Simulate adding website
                  setTimeout(() => {
                    setAgent(prev => ({
                      ...prev,
                      websiteKnowledge: [...(prev.websiteKnowledge || []), {
                        id: Date.now().toString(),
                        url: websiteUrl,
                        addedAt: new Date().toISOString(),
                        status: 'indexed'
                      }]
                    }));
                    setWebsiteUrl('');
                    setAddingWebsite(false);
                  }, 1500);
                }
              }}
              disabled={!websiteUrl || addingWebsite}
              className="w-full py-3 bg-teal-400 text-black font-medium rounded-lg hover:bg-teal-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addingWebsite ? 'Adding...' : 'Add to Knowledge Base'}
            </button>
          </div>
        </section>
      </div>

      {/* Files List */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-white">Knowledge Base Files</h2>
            <p className="text-gray-500 text-sm mt-1">
              {agent.knowledgeBase.length} files • {formatFileSize(agent.knowledgeBaseSize)} / {maxSize} MB used
            </p>
          </div>
        </div>
        
        {/* Storage Limit Bar */}
        <div className="mb-6 p-3 bg-[#1a1a1f] rounded-lg">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-400">Storage Used</span>
            <span className={`${isAtLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-teal-400'}`}>
              {formatFileSize(agent.knowledgeBaseSize)} / {maxSize} MB
            </span>
          </div>
          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${isAtLimit ? 'bg-red-400' : isNearLimit ? 'bg-yellow-400' : 'bg-teal-400'}`}
              style={{ width: `${Math.min(100, usedPercentage)}%` }}
            />
          </div>
        </div>
        
        {agent.knowledgeBase.length === 0 && (!agent.websiteKnowledge || agent.websiteKnowledge.length === 0) ? (
          <div className="text-center py-12">
            <Database className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Knowledge Files</h3>
            <p className="text-gray-500 text-sm">
              Upload PDFs or add website URLs to train your agent
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* PDF Files */}
            {agent.knowledgeBase.map((file) => (
              <div 
                key={file.id}
                className="flex items-center gap-4 bg-[#1a1a1f] border border-gray-700 rounded-lg p-4"
              >
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                  <File className="w-5 h-5 text-teal-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{file.name}</p>
                  <p className="text-gray-500 text-sm uppercase">{file.type || 'APPLICATION/PDF'}</p>
                </div>
                <div className="text-gray-500 text-sm">{formatFileSize(file.size)}</div>
                <div className="text-gray-500 text-sm">{new Date(file.uploadedAt || Date.now()).toLocaleDateString()}</div>
                <div className="flex items-center gap-2">
                  <button className="px-4 py-1.5 border border-teal-500 text-teal-400 rounded-lg hover:bg-teal-500/10 transition-colors text-sm flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Attach
                  </button>
                  <button className="px-4 py-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
                <button
                  onClick={() => removeKnowledgeFile(file.id)}
                  className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            {/* Website URLs */}
            {agent.websiteKnowledge?.map((website) => (
              <div 
                key={website.id}
                className="flex items-center gap-4 bg-[#1a1a1f] border border-gray-700 rounded-lg p-4"
              >
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{website.url}</p>
                  <p className="text-gray-500 text-sm">WEBSITE</p>
                </div>
                <div className="text-gray-500 text-sm">{new Date(website.addedAt).toLocaleDateString()}</div>
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Indexed</span>
                </div>
                <button
                  onClick={() => setAgent(prev => ({
                    ...prev,
                    websiteKnowledge: prev.websiteKnowledge.filter(w => w.id !== website.id)
                  }))}
                  className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 p-4 bg-[#1a1a1f] border border-gray-700 rounded-lg">
          <h4 className="text-white font-medium mb-2">Tips for better results</h4>
          <ul className="space-y-1 text-gray-400 text-sm">
            <li>• Use structured documents with clear headings</li>
            <li>• Include FAQs and common questions</li>
            <li>• Add product/service descriptions</li>
            <li>• Keep files under 10MB each for faster processing</li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeBaseTab;
